import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';

import {
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  DataAccessProductsFacade,
  DataAccessProductsPageFacade,
} from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade, SliderConfig } from '@planone/bau365/data-access-screensize';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import { LinkClickFacade, ShareFacade, TabChangeFacade } from '@planone/bau365/feature-tracking';
import { Exhibitor, Product } from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { SWIPER_INTERACTION_EMITTER, SwiperFacade, SwiperInteractionEvent } from '@planone/shared/data-access-swiper';
import {
  BreadcrumbElement,
  ModalBottomsheetHelper,
  ModalBottomsheetType,
  ShareButtonsHelper,
} from '@planone/shared/ui-elements';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { ImageFileTypes, ImageProviders } from '@planone/shared/util-pipelines';
import { filterIsDefined } from '@planone/shared/util-rxjs-operator-type-helper';
import { BreadcrumbData, BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

import { CountryAvailabilityModalComponent } from './country-availability-modal/country-availability-modal.component';

@Component({
  selector: 'bau365-page-product-details',
  templateUrl: './page-product-details.component.html',
  styleUrls: ['./page-product-details.component.scss'],
})
export class PageProductDetailsComponent implements OnInit, OnDestroy {
  product$: Observable<Product | undefined>;
  productLoading$: Observable<boolean | undefined>;
  productError$: Observable<string | undefined>;
  exhibitor$: Observable<Exhibitor | undefined>;
  products$: Observable<Product[] | undefined>;
  size$: Observable<string | undefined>;
  cardSliderConfig$: Observable<SliderConfig | null | undefined>;
  fairId$: Observable<string>;
  isBauInsights$: Observable<boolean>;
  featureProductAtBooth$: Observable<boolean>;

  tabIndex = 0;
  socialShareUrl: string | undefined;
  size: string | undefined;
  breadcrumbElements: BreadcrumbElement[] | undefined;
  product: Product | undefined = undefined;
  exhibitor: Exhibitor | undefined = undefined;
  emailSubject = localizedMeta['shareEmailSubject'];
  emailBody = localizedMeta['shareEmailBody'];
  ImageFileTypes = ImageFileTypes;
  referencedProducts: Product[] | undefined;

  private unsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('shareButtonsTitle') public shareButtonsTitle: TemplateRef<never> | null | undefined;
  @ViewChild('shareButtons') public shareButtons: TemplateRef<never> | null | undefined;

  constructor(
    public dialog: MatDialog,
    private productsFacade: DataAccessProductsFacade,
    private productsPageFacade: DataAccessProductsPageFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    screensizeFacade: DataAccessScreensizeFacade,
    private trackingTabChangeFacade: TabChangeFacade,
    private trackingLinkClickFacade: LinkClickFacade,
    private trackingShareFacade: ShareFacade,
    private SwiperFacade: SwiperFacade,
    private fairsFacade: DataAccessFairsFacade,
    private activatedRoute: ActivatedRoute,
    private metaService: MetaService,
    private shareButtonsHelper: ShareButtonsHelper,
    private modalBottomsheetHelper: ModalBottomsheetHelper,
    private structuredDataGenerator: BreadcrumbGenerator,
    private renderer2: Renderer2,
    private breadcrumbHelper: BreadcrumbHelper,
    public TogglesFacade: DataAccessTogglesFacade,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(SWIPER_INTERACTION_EMITTER) private swiperInteractionEmitter: BehaviorSubject<SwiperInteractionEvent>
  ) {
    this.metaService.setTitle(localizedMeta['titleProductDetails'], [$localize`:@@pageMeta-titles-loading: Lädt...`]);

    this.fairId$ = this.fairsFacade.fairId$;
    this.product$ = productsFacade.product$;
    this.productLoading$ = productsFacade.productLoading$;
    this.productError$ = productsFacade.productError$;
    this.products$ = productsFacade.productProducts$;
    this.size$ = screensizeFacade.size$;
    this.cardSliderConfig$ = screensizeFacade.cardSliderConfig$;
    this.exhibitor$ = this.product$.pipe(
      filterIsDefined(),
      switchMap((product) => {
        return (this.exhibitor$ = this.exhibitorsFacade.getExhibitorById(product.exhibitor_id));
      })
    );
    this.isBauInsights$ = this.fairsFacade.isBauInsights$;
    this.featureProductAtBooth$ = this.fairsFacade.fairConfig$.pipe(
      map((fairConfig) => fairConfig.productAtBooth ?? false)
    );
  }

  ngOnInit() {
    this.fairsFacade.fairConfig$.pipe(takeUntil(this.unsubscribe)).subscribe((fair) => {
      this.metaService.addOpenGraphMetaTags(
        localizedMeta['linkPreviewTitleProductPage'],
        localizedMeta['linkPreviewDescriptionProductPage'],
        this.document.URL,
        this.baseHref + 'assets/link-preview-img.png',
        [fair.name]
      );
    });

    this.swiperInteractionEmitter.pipe(takeUntil(this.unsubscribe)).subscribe((event: SwiperInteractionEvent) => {
      this.swiperInteraction(event);
    });

    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.productsPageFacade.pageView(params['id'], params['fair_id']);
      this.productsFacade.pageOpened(params['id']);
    });

    this.product$.pipe(takeUntil(this.unsubscribe)).subscribe((product) => {
      this.product = product;
      this.emailBody += $localize`:@@pageProduct-emailShare-entityType-product:Produkt ${this.product?.name}`;
    });

    this.products$.pipe(takeUntil(this.unsubscribe)).subscribe((products) => {
      this.referencedProducts = products;
      this.shuffleProducts();
    });

    this.exhibitor$.pipe(takeUntil(this.unsubscribe)).subscribe((exhibitor) => {
      this.exhibitor = exhibitor;
    });

    this.size$.pipe(takeUntil(this.unsubscribe)).subscribe((size) => (this.size = size));

    combineLatest(this.product$, this.exhibitor$)
      .pipe(withLatestFrom(this.fairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([[product, exhibitor], fair]) => {
        if (product && exhibitor) {
          this.socialShareUrl = this.shareButtonsHelper.buildShareUrlFromPath(
            'fairs/' + exhibitor.fair_id + '/products/' + product.id
          );

          const breadcrumbData: BreadcrumbData = this.breadcrumbHelper.prepareForProduct(fair, exhibitor, product);

          this.breadcrumbElements = breadcrumbData.elements;

          this.structuredDataGenerator.validateBreadcrumbStructuredData(
            this.renderer2,
            breadcrumbData.structuredData,
            this.document.head
          );
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();

    this.metaService.removeOpenGraphMetaTags();
    this.structuredDataGenerator.removeTag(this.renderer2, this.document.head);
  }

  openCountryAvailabilityModal() {
    this.dialog.open(CountryAvailabilityModalComponent, {
      maxWidth: '400px',
    });
  }

  trackDownload(element: EventTarget | null, fileName: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.product && this.exhibitor) {
        this.productsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkText: fileName,
          linkType: 'DOWNLOAD',
          exhibitor: this.exhibitor,
          product: this.product,
          context: this.exhibitor.fair_id,
          pageType: 'product-details',
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'PRODUCT',
          linkText: fileName,
          linkType: 'DOWNLOAD',
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'product-details',
          eventTarget: element ?? undefined,
        });
      }
    });
  }

  trackClick(element: EventTarget | null, linkType: string, linkText?: string, contactPersonId?: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.product && this.exhibitor) {
        this.productsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkType: linkType,
          exhibitor: this.exhibitor,
          product: this.product,
          context: this.exhibitor.fair_id,
          pageType: 'product-details',
          ...(linkText && { linkText: linkText }),
          ...(contactPersonId && { contactPersonId: contactPersonId }),
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'PRODUCT',
          linkType: linkType,
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'product-details',
          eventTarget: element ?? undefined,
        });
      }
    });
  }

  openShareButtonDialog(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.product) {
        this.productsPageFacade.shareDialogOpened('PRODUCT', 'product-details', fairId, this.product);

        this.trackingShareFacade.open({
          entityType: 'PRODUCT',
          pageType: 'product-details',
          context: fairId,
        });
      }

      this.modalBottomsheetHelper.openDialog(
        ModalBottomsheetType.share,
        this.shareButtonsTitle,
        this.shareButtons,
        this.size
      );
    });
  }

  shareButtonClick(network: string) {
    if (this.product && this.exhibitor && this.exhibitor.fair_id) {
      this.productsPageFacade.shareButtonClicked(
        network,
        this.product,
        this.exhibitor.name,
        this.exhibitor.fair_id,
        'product-details'
      );

      this.trackingShareFacade.click({
        entityType: 'PRODUCT',
        pageType: 'product-details',
        context: this.exhibitor.fair_id,
        network: network,
      });
    }
  }

  tabChange(event: MatTabChangeEvent) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      this.productsPageFacade.tabClicked({
        tabLabel: event.tab.ariaLabel,
        pageType: 'product-details',
        context: this.exhibitor?.fair_id ?? fairId,
        exhibitor: this.exhibitor,
        product: this.product,
      });

      this.trackingTabChangeFacade.trigger({
        context: this.exhibitor?.fair_id ?? fairId,
        pageType: 'product-details',
        tabLabel: event.tab.ariaLabel,
        entityType: 'PRODUCT',
      });
    });
  }

  swiperInteraction(event: SwiperInteractionEvent) {
    const slide = (this.product?.images ?? [])[event.index];
    const count = (this.product?.images ?? []).length;

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (slide) {
        this.SwiperFacade.interaction({
          interaction_type: event.type,
          image_resource: slide.resource,
          image_provider: <ImageProviders>slide.provider,
          image_description: slide.description,
          image_position: event.index + 1,
          context: this.exhibitor?.fair_id ?? fairId,
          page_type: 'product-details',
          exhibitor: this.exhibitor,
          product: this.product,
          image_count: count,
        });
      }
    });
  }

  shuffleProducts() {
    if (this.referencedProducts) {
      for (let i = this.referencedProducts.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.referencedProducts[i], this.referencedProducts[j]] = [
          this.referencedProducts[j],
          this.referencedProducts[i],
        ];
      }
    }
  }
}
